import { Loader2 } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { cn } from '../../lib/cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'icon'

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover shadow-subtle',
  secondary: 'bg-surface text-foreground border border-border hover:bg-surface-hover',
  ghost: 'text-foreground-muted hover:bg-surface-hover hover:text-foreground',
  danger: 'bg-danger text-white hover:opacity-90',
}

const SIZES: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-xs gap-1.5',
  md: 'h-9 px-3.5 text-sm gap-2',
  icon: 'h-9 w-9 justify-center',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  isLoading?: boolean
  leadingIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    leadingIcon,
    children,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // aria-busy rather than swapping the label: a screen reader announcing a
      // changed button name mid-submit reads as a different control.
      aria-busy={isLoading || undefined}
      disabled={props.disabled ?? isLoading}
      className={cn(
        'inline-flex select-none items-center rounded-md font-medium transition-colors',
        'disabled:pointer-events-none disabled:opacity-55',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {isLoading ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : (leadingIcon ?? null)}
      {children}
    </button>
  )
})
